import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Logo } from "@/components/layout/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, Mail, CheckCircle2, RefreshCw } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email, origin: window.location.origin });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Something went wrong");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-navy text-white p-12 relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=1200&q=80" alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-br from-navy-deep to-navy/60" />
        <div className="relative"><Logo light /></div>
        <div className="relative">
          <h2 className="headline text-5xl">Forgot your <span className="text-orange">password?</span></h2>
          <p className="text-white/70 mt-4 max-w-md">No worries — we'll email you a secure link to set a new one.</p>
        </div>
        <div className="relative text-white/40 text-xs">© 2026 Elite by Thotis Media</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-sm" data-testid="forgot-form">
          <div className="lg:hidden mb-8"><Logo /></div>
          {sent ? (
            <div className="animate-fade-up text-center">
              <div className="h-14 w-14 rounded-2xl bg-green-100 flex items-center justify-center mx-auto mb-4"><CheckCircle2 className="h-7 w-7 text-green-600" /></div>
              <h1 className="headline text-3xl text-navy">Check your email</h1>
              <p className="text-muted-foreground mt-3">If an account exists for <b>{email}</b>, a reset link is on its way. The link expires in 30 minutes and can be used only once.</p>
              <Link to="/login" data-testid="back-to-login" className="inline-block mt-6 font-bold text-navy hover:text-orange">← Back to login</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="animate-fade-up">
              <div className="eyebrow text-xs text-orange mb-2">Password reset</div>
              <h1 className="headline text-4xl text-navy mb-6">Reset password</h1>
              {error && <div className="mb-4 text-sm text-destructive bg-destructive/10 px-4 py-3 rounded-xl">{error}</div>}
              <div className="flex items-center gap-3 p-4 rounded-2xl bg-orange/10 mb-4">
                <Mail className="h-5 w-5 text-orange" />
                <p className="text-sm text-navy">Enter your email and we'll send you a reset link.</p>
              </div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-12 rounded-xl" placeholder="you@example.com" />
              <Button type="submit" data-testid="forgot-submit" disabled={loading} className="w-full mt-6 h-12 rounded-full bg-orange hover:bg-orange-dark text-white font-display font-bold italic uppercase tracking-wide">
                {loading ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Sending…</> : <>Send reset link <ArrowRight className="h-4 w-4 ml-1" /></>}
              </Button>
              <p className="text-center text-sm text-muted-foreground mt-6">
                Remembered it? <Link to="/login" className="font-bold text-navy hover:text-orange">Log in</Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
